import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Users, Plus, Trash2, Building2, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES_SAAS = [
  { value: "admin", label: "Administrador de empresa" },
  { value: "ventas", label: "Ventas" },
  { value: "coordinacion", label: "Coordinación" },
  { value: "finanzas", label: "Finanzas" },
];

const ROLES_OPERATIVOS = [
  { value: "chef", label: "Chef" },
  { value: "mesero", label: "Mesero" },
  { value: "coordinacion", label: "Coordinación" },
  { value: "operacion", label: "Operación" },
  { value: "cliente", label: "Cliente" },
];

/**
 * Panel de administración de usuarios del propio tenant.
 * Solo accesible por Admin de empresa o Superadmin. Aisla estrictamente a la
 * `empresa_id` del usuario logueado (el backend valida). Respeta el límite
 * de usuarios del plan (Organiza=2, Opera=5, Negocio=ilimitado).
 */
export default function AdminUsuariosTenant() {
  const [usuarios, setUsuarios] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [plan, setPlan] = useState(null);
  const [uso, setUso] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", workspace_rol: "ventas", tipo: "saas" });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [u, e] = await Promise.all([
        axios.get(`${API}/tenant/usuarios`),
        axios.get(`${API}/tenant/mi-empresa`),
      ]);
      setUsuarios(u.data || []);
      setEmpresa(e.data?.empresa || null);
      setPlan(e.data?.plan || null);
      setUso(e.data?.uso || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando usuarios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const crear = async () => {
    if (!form.email || !form.password) {
      toast.error("Email y contraseña son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const { tipo, ...body } = form;
      // Iter120 · Endpoint según tipo:
      //   saas       → consume seat, sujeto al límite del plan
      //   operativo  → NO consume seat (personal interno)
      const endpoint = tipo === "operativo" ? `${API}/tenant/usuarios` : `${API}/tenant/usuarios/invitar`;
      await axios.post(endpoint, body);
      toast.success(tipo === "operativo" ? "Personal operativo agregado" : "Colaborador SaaS invitado");
      setShowModal(false);
      setForm({ name: "", email: "", password: "", workspace_rol: "ventas", tipo: "saas" });
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el usuario");
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async (uid, email) => {
    if (!window.confirm(`¿Quitar acceso a ${email}? Podrá volver a ser invitado.`)) return;
    try {
      await axios.delete(`${API}/tenant/usuarios/${uid}`);
      toast.success("Acceso revocado");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const limUsuarios = uso?.usuarios?.limite;
  const useUsuarios = uso?.usuarios?.actual ?? 0;
  const seatsLleno = limUsuarios !== -1 && limUsuarios != null && useUsuarios >= limUsuarios;
  // Bloqueo solo si el usuario está intentando invitar SaaS y ya llenó el límite.
  // Personal operativo NUNCA se bloquea por límite de seats.
  const bloqueado = seatsLleno && form.tipo === "saas";

  return (
    <div className="space-y-6" data-testid="pagina-usuarios-tenant">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-display" style={{ color: "var(--brand-text)" }}>
            Usuarios de la empresa
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Administra el equipo que puede acceder al ERP. El aislamiento entre empresas es estricto.
          </p>
        </div>
        <Button onClick={() => setShowModal(true)} data-testid="btn-nuevo-usuario">
          <Plus className="w-4 h-4 mr-2" /> Invitar usuario
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <Card data-testid="card-empresa">
          <CardContent className="p-4 flex items-center gap-3">
            <Building2 className="w-6 h-6 text-rose-700" />
            <div>
              <p className="text-xs uppercase text-stone-500">Empresa</p>
              <p className="text-sm font-medium truncate">{empresa?.nombre || "—"}</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="card-plan">
          <CardContent className="p-4 flex items-center gap-3">
            <Shield className="w-6 h-6 text-emerald-700" />
            <div>
              <p className="text-xs uppercase text-stone-500">Plan</p>
              <p className="text-sm font-medium">{plan?.nombre || "—"}</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="card-uso-usuarios">
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="w-6 h-6 text-amber-700" />
            <div>
              <p className="text-xs uppercase text-stone-500">Usuarios</p>
              <p className="text-sm font-medium">
                {useUsuarios} {limUsuarios === -1 || limUsuarios == null ? "· ilimitados" : `de ${limUsuarios}`}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {bloqueado && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="warn-limite">
          Alcanzaste el límite de usuarios del plan <b>{plan?.nombre}</b>. Actualiza tu plan para invitar más miembros.
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Equipo</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-stone-500">Cargando…</p>
          ) : usuarios.length === 0 ? (
            <p className="text-sm text-stone-500">Aún no tienes usuarios. Invita al primero.</p>
          ) : (
            <ul className="divide-y">
              {usuarios.map(u => (
                <li key={u.user_id} className="py-3 flex items-center justify-between gap-3" data-testid={`row-usuario-${u.user_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{u.name || u.email}</p>
                    <p className="text-xs text-stone-500 truncate">{u.email} · {u.workspace_rol || u.rol_interno || "sin rol"}</p>
                  </div>
                  {(u.workspace_rol !== "admin" || empresa?.dueno_user_id !== u.user_id) && (
                    <Button variant="ghost" size="sm" onClick={() => eliminar(u.user_id, u.email)} data-testid={`btn-eliminar-${u.user_id}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-nuevo-usuario">
          <DialogHeader><DialogTitle>Invitar usuario</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo de invitación</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setForm({...form, tipo: "saas", workspace_rol: "ventas"})}
                  className={`px-3 py-2 rounded-md border text-sm ${form.tipo === "saas" ? "bg-rose-50 border-rose-400 text-rose-800 font-medium" : "bg-white border-stone-300"}`}
                  data-testid="btn-tipo-saas"
                >
                  Colaborador SaaS<br/><span className="text-xs opacity-70">Consume seat del plan</span>
                </button>
                <button
                  type="button"
                  onClick={() => setForm({...form, tipo: "operativo", workspace_rol: "mesero"})}
                  className={`px-3 py-2 rounded-md border text-sm ${form.tipo === "operativo" ? "bg-emerald-50 border-emerald-400 text-emerald-800 font-medium" : "bg-white border-stone-300"}`}
                  data-testid="btn-tipo-operativo"
                >
                  Personal operativo<br/><span className="text-xs opacity-70">NO consume seat</span>
                </button>
              </div>
            </div>
            {bloqueado && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="warn-limite-modal">
                Alcanzaste el límite de {limUsuarios} usuarios SaaS del plan {plan?.nombre}. Solo podrás agregar personal operativo o actualizar tu plan.
              </div>
            )}
            <div>
              <Label>Nombre</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nombre completo" data-testid="input-nombre" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="persona@empresa.com" data-testid="input-email" />
            </div>
            <div>
              <Label>Contraseña temporal *</Label>
              <Input type="text" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Mínimo 6 caracteres" data-testid="input-password" />
            </div>
            <div>
              <Label>Rol {form.tipo === "saas" ? "SaaS" : "operativo"}</Label>
              <select
                value={form.workspace_rol}
                onChange={e => setForm({...form, workspace_rol: e.target.value})}
                className="w-full h-10 rounded-md border px-3 text-sm bg-white"
                data-testid="select-rol"
              >
                {(form.tipo === "saas" ? ROLES_SAAS : ROLES_OPERATIVOS).map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 flex-col-reverse sm:flex-row">
            <Button variant="outline" onClick={() => setShowModal(false)} data-testid="btn-cancelar">Cancelar</Button>
            <Button onClick={crear} disabled={saving || bloqueado} data-testid="btn-guardar-usuario">
              {saving ? "Creando…" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
